import React, { useState, useEffect } from 'react';

import ImgFullScreen from '../../img/fullscreen.png';
import LogoMafra from "../../img/logo-mafra-surgical.svg";
import MelTotem from '../../img/mel-totem.png';

import { CustomerScore } from 'components/Totem/CustomerScore';
import { lowScoreEmoticons } from 'components/Totem/interface';
import { Visibility } from 'components/Visibility';
import { CustomerFeedback } from 'components/Totem/CustomerFeedback';

import { useCheckConnection } from 'hooks/useCheckConnection';
import { useQuery } from 'hooks/useQuery';

import { StoreTotemPostProps, StoreTotemProps } from 'models/StoreTotem';
import StoreTotemService from 'services/storeTotem.service';

import { CustomerStepsType, ErrorPageProps } from './interface';

import {
  Container,
  Content,
  ContentRating,
  ContentThanks,
  FullScreen,
  Logo,
  Mel,
  Section,
  TextCenter
} from './styles';

export const Totem = () => {

  const [dataStoreTotem, setDataStoreTotem] = useState<StoreTotemProps>();
  const [errorPage, setErrorPage] = useState<ErrorPageProps>({
    isError: false,
    message: ""
  });
  const [customerStage, setCustomerStage] = useState<string>(CustomerStepsType.CustomerScore);
  const [customerResponseStep1, setCustomerResponseStep1] = useState<string>("");
  const [customerResponseStep2, setCustomerResponseStep2] = useState<string>("");

  const REVIEWS_KEY = "@CCARE_TOTEM_CUSTOMER_REVIEWS";

  const tempLocalStorage: StoreTotemPostProps[] = JSON.parse(localStorage.getItem(REVIEWS_KEY) || '[]');
  const onlineStatus = useCheckConnection();
  const keyParams: string | null = useQuery().get('key');

  const _storeTotemService = new StoreTotemService();

  useEffect(() => {
    getStoreTotem();
  }, []);

  useEffect(() => {
    if (onlineStatus && Object.entries(tempLocalStorage).length === 0)
      return;

    sendTotemDataAndClearLocalStorage();

  }, [onlineStatus]);

  useEffect(() => {
    if (customerResponseStep1) {
      changeStepIfLowScoreElseSendData();
    }
  }, [customerResponseStep1]);

  const getStoreTotem = async () => {
    await _storeTotemService.storeTotem(keyParams || "")
      .then((result: any) => {
        if (result && result.data)
          setDataStoreTotem(result.data);
      })
      .catch(() => {
        setErrorPage({
          isError: true,
          message: "Loja não encontrada."
        });
      });
  };

  const sendTotemDataAndClearLocalStorage = () => {
    if (onlineStatus && Object.entries(tempLocalStorage).length > 0)
      sendDataToBackendStatusOnline(true);
  };

  const changeStepIfLowScoreElseSendData = () => {
    if (lowScoreEmoticons.includes(customerResponseStep1))
      setCustomerStage(CustomerStepsType.CustomerFeedback);
    else
      sendCustomerDataCollection();
  };

  const sendCustomerDataCollection = async (noOption: boolean = false) => {
    changeStepAccordingToParameter(noOption);

    if (onlineStatus)
      sendDataToBackendStatusOnline();

    if (!onlineStatus)
      sendDataToLocalStorageStatusOffline();
  };

  const changeStepAccordingToParameter = (noOption: boolean) => {
    if (noOption)
      setCustomerStage(CustomerStepsType.CustomerScore);
    else {
      setCustomerStage(CustomerStepsType.CustomerThanks);
      redirectHomeAfterTimeout();
    }
  };

  const sendDataToBackendStatusOnline = async (fromLocalStorage: boolean = false) => {

    const stateData = [{
      answerQuestionOne: firstCapitalLetter(customerResponseStep1?.toLocaleLowerCase()),
      answerQuestionTwo: customerResponseStep2,
      createdDateAnswer: new Date()
    }];

    const dataFinal = fromLocalStorage ? tempLocalStorage : stateData;
    const response = await _storeTotemService.postStoreTotem(dataFinal, dataStoreTotem?.key || "");

    clearClientResponses();

    if (response.status === 200 && fromLocalStorage)
      localStorage.removeItem(REVIEWS_KEY);

    if (response.status !== 200) {
      setErrorPage({
        ...errorPage,
        isError: true,
        message: "Ocorreu um erro durante a requisição.",
      });
    }
  };

  const sendDataToLocalStorageStatusOffline = () => {
    if (!customerResponseStep1) return;

    tempLocalStorage.push({
      answerQuestionOne: firstCapitalLetter(customerResponseStep1?.toLocaleLowerCase()),
      answerQuestionTwo: customerResponseStep2,
      createdDateAnswer: new Date()
    });

    localStorage.setItem(REVIEWS_KEY, JSON.stringify(tempLocalStorage));

    clearClientResponses();
  };

  const redirectHomeAfterTimeout = () => {
    const timerThanks = setTimeout(() => {
      setCustomerStage(CustomerStepsType.CustomerScore);
    }, dataStoreTotem?.answerTimeout);
    return () => clearTimeout(timerThanks);
  };

  const clearClientResponses = () => {
    setCustomerResponseStep1("");
    setCustomerResponseStep2("");
  };

  const firstCapitalLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const customerIsInStep1 = customerStage === CustomerStepsType.CustomerScore;
  const customerIsInStep2 = customerStage === CustomerStepsType.CustomerFeedback;
  const customerIsInStep3 = customerStage === CustomerStepsType.CustomerThanks;

  const existStore = dataStoreTotem?.enabled || false;

  const makeFullScreen = () => {
    const divFullScreen = document.getElementById("fullscreenEnabled");

    if (!document.fullscreenElement) {
      divFullScreen?.requestFullscreen();
      return;
    }

    document.exitFullscreen();
  };

  return (
    <Section id="fullscreenEnabled">

      <FullScreen
        src={ImgFullScreen}
        onClick={() => makeFullScreen()}
      />

      <Container>
        <Content>
          <div>
            <Mel src={MelTotem} alt="Mel" />
          </div>

          <Visibility visible={errorPage.isError}>
            <ContentThanks>
              <TextCenter> {errorPage?.message}</TextCenter>
            </ContentThanks>
          </Visibility>

          <Visibility visible={existStore && !errorPage.isError}>
            <ContentRating>
              <Visibility visible={customerIsInStep1}>
                <CustomerScore setCustomerResponseScore={setCustomerResponseStep1} />
              </Visibility>

              <Visibility visible={customerIsInStep2}>
                <CustomerFeedback
                  setCustomerResponseFeedback={setCustomerResponseStep2}
                  customerResponseFeedback={customerResponseStep2}
                  sendCustomerDataCollection={sendCustomerDataCollection}
                  dataStoreTotem={dataStoreTotem}
                />
              </Visibility>

              <Visibility visible={customerIsInStep3}>
                <ContentThanks>
                  <TextCenter> Agradecemos a sua visita!</TextCenter>
                </ContentThanks>
              </Visibility>

              <Logo src={LogoMafra} width={249} height={88} />

            </ContentRating>
          </Visibility>
        </Content>
      </Container>
    </Section>
  );
};